/**
 * The six problems the solution-led message is built on, and what we do about
 * each one.
 *
 * ONE definition, two pages. The home page renders the short form as cards
 * (`ProblemCards`); `/solutions` renders the long form as sections and uses the
 * same `id` as its anchor, so a card links straight to the section that expands
 * it. Keeping both in this file is what makes that link safe: `SOLUTION_DETAIL`
 * is typed as a `Record<ProblemId, ...>`, so adding a seventh problem fails to
 * compile until its section exists, and `problems.test.ts` checks the rendered
 * anchors rather than trusting the types alone.
 *
 * The cards previously linked to `#book`, the booking CTA. The label promised an
 * explanation and delivered a request for a call, which is the bait these cards
 * exist to avoid, so the links were removed on 2026-08-02 pending a real page to
 * point at. This is that page.
 */

export type Problem = {
  id: string;
  /** Card heading, and the section heading on /solutions. */
  title: string;
  /** How a prospect describes the problem to us, in their words. */
  quote: string;
  /** The one-line answer. The card stops here; /solutions carries on. */
  answer: string;
};

export const PROBLEMS = [
  {
    id: "five-systems",
    title: "The truth lives in five systems",
    quote:
      "The stock system says one thing, the warehouse says another - and nobody's number agrees with the accounts.",
    answer:
      "Stock, jobs, orders and billing synced into one source of truth your whole team can query.",
  },
  {
    id: "runs-on-memory",
    title: "The business runs on memory",
    quote:
      "Reorders, approvals, chasing jobs and suppliers - it all depends on someone remembering to check.",
    answer:
      "Workflows watch your data and act: alerts, drafts and updates that fire without being asked.",
  },
  {
    id: "needs-a-specialist",
    title: "Every answer needs a specialist",
    quote:
      "If our reporting person is on leave, nobody can answer a simple question about the numbers.",
    answer:
      "Operational reports anyone can run - and plain-English answers from Lara when the report does not exist yet.",
  },
  {
    id: "chasing-debtors",
    title: "Chasing debtors eats the admin week",
    quote:
      "Statements go out, follow-ups get forgotten, and replies land in three different inboxes.",
    answer:
      "Automated statements and follow-ups - with replies threading back into one place.",
  },
  {
    id: "never-reconcile",
    title: "Sales never reconcile to the ledger",
    quote:
      "Takings, refunds and tender splits get re-captured by hand, and month-end is a hunt for the missing R400.",
    answer:
      "Daily automated feeds post balanced journals to your accounting system - reconciled before you have had coffee.",
  },
  {
    id: "month-end-entities",
    title: "Month-end across entities takes weeks",
    quote:
      "Three companies, three ledgers, one giant consolidation spreadsheet that only one person understands.",
    answer:
      "Every entity lands in one reporting database - consolidated dashboards that are always current.",
  },
] as const satisfies readonly Problem[];

export type ProblemId = (typeof PROBLEMS)[number]["id"];

/** Where a problem's section sends a reader who wants the detail. */
type SolutionLink = {
  to: string;
  label: string;
  /** Set for links to an anchor on another page. */
  hash?: string;
};

export type SolutionDetail = {
  /**
   * What actually changes for the business. Written as an outcome rather than a
   * feature list - the products come later on the page, deliberately.
   */
  lead: string;
  /** The concrete steps, in the order they happen. */
  how: readonly string[];
  /** Where to read more. Every target is a page that already exists. */
  links: readonly SolutionLink[];
};

/**
 * Typed as a total record on purpose: a new entry in `PROBLEMS` breaks the build
 * here until someone has written what we actually do about it. A partial map
 * would instead render a section with an empty body.
 */
export const SOLUTION_DETAIL: Record<ProblemId, SolutionDetail> = {
  "five-systems": {
    lead: "One number, agreed by everyone, without anyone re-keying it. Your systems keep doing their jobs; their data lands together in a database you own, so stock, jobs, orders and billing can finally be read side by side.",
    how: [
      "Connectors pull from each system on a schedule, cloud or on-premise, with every run monitored.",
      "The data lands in your own cloud database - not a black box we hold on your behalf.",
      "It arrives as clean, logical tables built for reporting, so Power BI, Excel or your own SQL can read it directly.",
    ],
    links: [
      { to: "/connectors/product", label: "How connectors work" },
      { to: "/products/myapp", label: "Capture what no system owns" },
    ],
  },
  "runs-on-memory": {
    lead: "The checking stops being a person's job. Workflows watch the data for the conditions you care about and act on them, so nothing waits on somebody remembering it on a Friday afternoon.",
    how: [
      "You describe what should happen in plain English. We build it - there is no builder for your team to learn.",
      "We dry-run it against your own data first, so you can see exactly what it would have done before it does anything.",
      "It goes live once you are happy, and keeps running whether or not anyone is watching.",
    ],
    links: [
      { to: "/", hash: "workflow", label: "See workflow examples" },
      { to: "/proof-of-concept", label: "Prove it on your data first" },
    ],
  },
  "needs-a-specialist": {
    lead: "Answers stop being a queue. The routine questions get operational reports anyone can run, and the unpredictable ones get asked in plain English against your own data, with the source cited.",
    how: [
      "Reports your team runs themselves, built once against the reporting database.",
      "Knowledge Base answers questions of your documents and databases in plain English, and shows where each answer came from.",
      "Privacy-first: your data stays in the environment you choose to host in.",
    ],
    links: [
      { to: "/products/knowledge-base", label: "About Knowledge Base" },
      { to: "/proof-of-concept", label: "Ask it a real question" },
    ],
  },
  "chasing-debtors": {
    lead: "The follow-up happens on its own, and the replies land in one place. The admin week gets its hours back without anyone losing sight of who owes what.",
    how: [
      "Statements and reminders go out on a schedule driven by what the ledger actually says.",
      "Follow-ups escalate on your rules rather than on whoever happens to remember.",
      "Replies thread back into one inbox, so the conversation is not scattered across three mailboxes.",
    ],
    links: [
      { to: "/connectors/product", label: "Connect your accounting system" },
      { to: "/contact", label: "Talk through your process" },
    ],
  },
  "never-reconcile": {
    lead: "Month-end stops being a hunt. Daily feeds post balanced journals into your accounting system, so takings, refunds and tender splits are already reconciled by the time anyone looks.",
    how: [
      "Point-of-sale and sales feeds are read automatically, every day, without a manual upload.",
      "They post as balanced journals that stay aligned with the source data.",
      "Differences surface as they happen rather than three weeks later.",
    ],
    links: [
      { to: "/connectors/product", label: "See the connector catalogue" },
      { to: "/connectors/xero-case-study", label: "Read a real integration" },
    ],
  },
  "month-end-entities": {
    lead: "Consolidation stops depending on one spreadsheet and the one person who understands it. Every entity lands in the same reporting database, so the group view is current rather than rebuilt.",
    how: [
      "Each entity's ledger syncs into one place on its own schedule.",
      "Consolidated dashboards read from that database, so they are never out of date by a month.",
      "The spreadsheet becomes something you check against, not something the close depends on.",
    ],
    links: [
      { to: "/connectors/product", label: "Multi-entity connectors" },
      { to: "/pricing", label: "What it costs" },
    ],
  },
};
